import { useEffect, useRef, useState } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Camera, X } from '@phosphor-icons/react';

/**
 * Reusable QR scanner.
 * Props:
 *   onScan(token: string)  — called with the decoded text
 *   onClose()              — user-cancelled
 *
 * Uses html5-qrcode under the hood. We pause the camera AS SOON AS we get a hit
 * so consecutive scans don't re-fire the same callback.
 */
const QrScanner = ({ onScan, onClose }) => {
  const elId = 'qr-scanner-' + (useRef(Math.random().toString(36).slice(2)).current);
  const scannerRef = useRef(null);
  const [err, setErr] = useState('');
  const handledRef = useRef(false);

  useEffect(() => {
    const scanner = new Html5Qrcode(elId);
    scannerRef.current = scanner;
    scanner.start(
      { facingMode: 'environment' },
      { fps: 10, qrbox: { width: 240, height: 240 } },
      (decoded) => {
        if (handledRef.current) return;
        handledRef.current = true;
        scanner.stop().finally(() => onScan(decoded));
      },
      () => { /* per-frame decode failures: ignore */ }
    ).catch(e => setErr(e?.message || 'Caméra inaccessible'));

    return () => {
      try {
        if (scanner.getState && scanner.getState() === 2 /* SCANNING */) {
          scanner.stop().catch(() => {});
        }
      } catch { /* ignore */ }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Card className="border-2 border-[#0052FF] shadow-lg" data-testid="qr-scanner">
      <CardContent className="p-3 space-y-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Camera size={18} className="text-[#0052FF]" weight="duotone" />
            <span className="font-semibold text-sm">Scan QR</span>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose} data-testid="qr-scanner-close">
            <X size={16} />
          </Button>
        </div>
        <div id={elId} style={{ width: '100%' }} />
        {err && <p className="text-xs text-red-600">{err}</p>}
      </CardContent>
    </Card>
  );
};

export default QrScanner;
